import { WalletConfig, WalletField } from "@/types/DocumentTemplate";
import {
  resolveLangKey,
  resolveLangKeyFromMap,
  resolveLangValue,
} from "@/lib/utils/resolve-lang";
import { extractTokens } from "@/lib/utils/variable-replacer";
import { SEAT_ENRICHED_KEYS } from "@/lib/utils/seat-variables";
import { EVENT_VARIABLE_KEYS } from "@/lib/utils/event-variables";

/**
 * The two decisions the PASS RENDERER makes that a preview cannot see from the
 * config alone — mirrored here so the builder can show them.
 *
 * <p>Both live in `main-backend`'s `PassJsonBuilder` and are pinned by
 * `PassJsonBuilderTest`; the cases in `__tests__/passRenderRules.test.ts` are
 * that suite's cases, ported one for one. Keeping them side by side is the
 * point: the customer's report was that the pass shows less than the builder
 * promised, and every item of that difference was one of these two rules
 * applied server-side and nowhere else.
 *
 * <p><b>The rule may only delete what the frontend can actually resolve.</b>
 * It removes rows, where the preview used to draw an em-dash, so every gap in
 * the FE's own substitution turns into a confident, invisible lie about the
 * delivered pass. Two guards keep it honest: language resolution as tolerant as
 * the renderer's ({@link resolveWalletPreviewLang}), and the caller's `knows`
 * predicate — derived from the guest object rather than from a maintained list
 * of token names, because every entry of such a list is a claim that has to
 * stay true (an audit found four holes in the list this replaced).
 */

/** The groups a pass field can live in, in the order Apple lays them out. */
export const FIELD_GROUPS = [
  "headerFields",
  "primaryFields",
  "secondaryFields",
  "auxiliaryFields",
  "backFields",
] as const;

export type FieldGroup = (typeof FIELD_GROUPS)[number];

/**
 * What the delivered pass will do with a row the client could not decide.
 *
 * `"awaited"` — the renderer fills it; this screen cannot name the value.
 * `"absent"`  — there is no value and both renderers delete the row.
 *
 * A row that is neither is not in this vocabulary at all: it either resolved,
 * or it is a deliberate label-only spacer, which the pass keeps as configured.
 */
export type RowCertainty = "awaited" | "absent";

export type DroppedField = {
  group: FieldGroup;
  /** What to call it to the operator — its label, or its key when unlabelled. */
  name: string;
};

const notBlank = (s: string | undefined | null): boolean =>
  s != null && s.trim().length > 0;

/**
 * Which language the preview should render, chosen the way the send chooses it.
 *
 * <p>Mirror of `WalletPassService.resolveWalletLanguage`: collect the union of
 * language keys the config actually carries, then pick the best match for the
 * recipient. Handing a raw `guest.language` straight to a map index instead is
 * what made the send dialog draw an empty pass — `resolve-lang.ts` opens with
 * the reason ("guest.language and template map keys are a mix of short and full
 * codes").
 */
export function resolveWalletPreviewLang(
  config: WalletConfig | undefined | null,
  guestLang: string | null | undefined,
  primaryLang: string,
): string {
  const available: string[] = [];
  const add = (map?: Record<string, string>) => {
    if (!map) return;
    for (const [key, value] of Object.entries(map)) {
      if (key && value && !available.includes(key)) available.push(key);
    }
  };
  add(config?.organizationName);
  add(config?.description);
  add(config?.logoText);
  for (const group of FIELD_GROUPS) {
    for (const field of config?.[group] ?? []) {
      add(field?.label);
      add(field?.value);
    }
  }
  // No localized text at all — nothing to disagree about.
  if (available.length === 0) {
    return notBlank(guestLang) ? guestLang!.trim() : primaryLang;
  }
  // Map-based, over the SORTED keys. The tag-list resolver next door lacks the
  // PRIMARY's own base step, so a pass whose primaryLanguage is de-DE but whose
  // config is keyed {de-AT, en-GB} rendered ENGLISH for a guest speaking
  // neither — an unrelated language beating a variant of the pass's own. Sorted
  // keys keep the map-based resolver's last resort ("first non-empty entry", in
  // insertion order) deterministic, which is what this function has always
  // promised.
  const sorted: Record<string, string> = {};
  for (const key of [...available].sort()) sorted[key] = "x";
  return (
    resolveLangKeyFromMap(sorted, guestLang, primaryLang) ??
    (notBlank(guestLang) ? guestLang!.trim() : primaryLang)
  );
}

/**
 * Resolve ONE language key on the raw map, then read both maps with it.
 *
 * <p>Two rules in one place. The renderer picks the language on the map as the
 * operator wrote it and substitutes afterwards; running the resolver over the
 * SUBSTITUTED map instead makes a token that failed for this guest look like a
 * missing translation, and it walks on to the next language — a German row
 * silently becomes English rather than being reported. And the last resort is
 * the SORTED first key, matching `resolveLangKeyFromMap`'s "must never pick
 * different arbitrary languages"; `Object.keys` order would depend on how the
 * config happened to be written.
 */
/**
 * The language key the RENDERER would read this map with.
 *
 * Sorted keys, and `resolveLangKeyFromMap` rather than `resolveLangValue`. The
 * difference only shows in the last resort — `resolveLangValue` takes the first
 * non-empty entry in INSERTION order, the backend takes it in SORTED order — but
 * that is enough to read a different language's text, and therefore to make a
 * different drop decision, for a config whose languages match neither the pass
 * language nor the primary.
 */
function pickLangKey(
  map: Record<string, string> | undefined,
  lang: string,
  primaryLang: string,
): string | null {
  const keys = Object.keys(map ?? {})
    .filter((k) => (map?.[k] ?? "").length > 0)
    .sort();
  if (keys.length === 0) return null;
  const sorted: Record<string, string> = {};
  for (const k of keys) sorted[k] = map?.[k] ?? "";
  return resolveLangKeyFromMap(sorted, lang, primaryLang) ?? keys[0]!;
}

function pickPair(
  rawMap: Record<string, string> | undefined,
  resolvedMap: Record<string, string> | undefined,
  lang: string,
  primaryLang: string,
): { raw: string; resolved: string } {
  const keys = Object.keys(rawMap ?? {})
    .filter((k) => (rawMap?.[k] ?? "").length > 0)
    .sort();
  if (keys.length === 0) return { raw: "", resolved: "" };
  // Map-based, over the SORTED keys, exactly like `resolveWalletPreviewLang`
  // above — the tag-list resolver has no base step for the PRIMARY language, so
  // a label keyed {az-AZ, de-AT} with primaryLang de-DE reported the AZERBAIJANI
  // text for a guest speaking neither, purely because it sorts first. The
  // backend twin (`DocumentTemplatePerGuestService.resolveLangValueFromMap`)
  // picks de-AT, and this decides which row the drop rule NAMES to the operator.
  // Real values, not placeholders: the filter above already removed the empty
  // ones, so the map is exactly the candidate set.
  const sorted: Record<string, string> = {};
  for (const k of keys) sorted[k] = rawMap?.[k] ?? "";
  const key = resolveLangKeyFromMap(sorted, lang, primaryLang) ?? keys[0]!;
  return { raw: rawMap?.[key] ?? "", resolved: resolvedMap?.[key] ?? "" };
}

/**
 * Remove the fields the renderer will leave out of `pass.json`.
 *
 * <p>Takes BOTH the resolved config (tokens already substituted for this guest)
 * and the raw one, because the rule needs both halves: whether the value is
 * empty NOW, and whether the operator configured a value at all. That
 * distinction is the whole rule. A field whose `{{firstName}}` came back empty
 * is the reported bug — the device draws a caption with a blank underneath, so
 * the renderer drops the row. A field with no value configured is a label-only
 * row someone built on purpose, and it survives.
 *
 * <p>Only meaningful on a RESOLVED config. In the design lane nothing is
 * substituted, every value still reads `{{token}}`, and nothing can be empty.
 */
/**
 * The renderer's own rule for deleting a whole field row, in one place.
 *
 * <p>Mirrors `PassJsonBuilder.putSection`: a row whose value resolves to
 * nothing ships neither its value NOR its label, unless the operator never
 * configured a value and did give a label — that shape is a deliberate spacer
 * and survives.
 *
 * <p>Extracted because the DEVICE view of the preview needs the same answer
 * and had been giving a different one: it suppressed the em-dash but drew the
 * label anyway, so a card whose only ink was an orphaned "PLATZ" claimed to be
 * showing what the guest gets. Two statements of one rule is how that happens;
 * this is the one statement.
 *
 * @param valueResolved what the value resolves to for this recipient
 * @param valueRaw      the value the operator configured, tokens and all
 * @param labelResolved what the label resolves to
 */
export function rendererDropsRow(
  valueResolved: string,
  valueRaw: string | undefined,
  labelResolved: string,
): boolean {
  return (
    valueResolved.length === 0 &&
    (notBlank(valueRaw) || labelResolved.length === 0)
  );
}

export function applyPassRenderRules(
  resolved: WalletConfig,
  raw: WalletConfig,
  opts: {
    lang: string;
    primaryLang: string;
    /** Whether the client can decide what this token resolves to for this
     *  guest — see `knowsToken` in `variable-replacer.ts`. */
    knows: (token: string) => boolean;
  },
): {
  config: WalletConfig;
  dropped: DroppedField[];
  /**
   * Rows KEPT because the client could not decide them, by group and key — the
   * same shape `applyEventTokenRules` returns.
   *
   * These are not a detail. `{{seatNumber}}` is enricher-gated: the server fills
   * it from the seating plan, `GUEST_FIELDS` carries only `seatInfo`, so no
   * guest object the cockpit holds can answer it. The row is kept, correctly —
   * but the guest lane then drew the builder's em-dash, the mark for "nothing
   * here yet", with no caption to explain it, while the delivered pass simply
   * had no such row. Reported as "PLATZ is on the card and not on the pass".
   * Collecting them lets the lane say so.
   */
  uncertain: Record<string, string[]>;
} {
  const { lang, primaryLang, knows } = opts;
  const dropped: DroppedField[] = [];
  const uncertain: Record<string, string[]> = {};
  const out: WalletConfig = { ...resolved };

  for (const group of FIELD_GROUPS) {
    const fields = resolved[group];
    if (!fields || fields.length === 0) continue;
    const rawGroup = raw[group];
    const kept: WalletField[] = [];
    fields.forEach((field, i) => {
      // The renderer skips a keyless field outright, before any of this.
      if (!field || !notBlank(field.key)) return;
      // Match by KEY, but only while the key identifies ONE field: the renderer
      // deduplicates repeats with a `_2` suffix, so a group may legitimately
      // hold two of them, and `find` would then judge both against the first
      // one's configuration — deleting the deliberate label-only row this rule
      // exists to protect. Index is the honest answer there, and the fallback
      // for a config whose keys were rewritten between the two.
      const matches = rawGroup?.filter((f) => f?.key === field.key) ?? [];
      const rawField = matches.length === 1 ? matches[0] : rawGroup?.[i];

      const value = pickPair(rawField?.value, field.value, lang, primaryLang);
      const label = pickPair(rawField?.label, field.label, lang, primaryLang);

      // Anything the client cannot decide for this guest: keep it.
      //
      // KEEPING still looks at both halves — an undecidable label makes the row
      // as a whole unjudgeable — but only the VALUE decides whether the row can
      // DROP: `rendererDropsRow` fires on an empty value and never on a label.
      // So a row whose label carries an unknown token while its value is a
      // literal constant is kept, correctly, and must NOT be reported as "drops
      // for guests without a value" — that sentence would be false about a row
      // that can never drop.
      const valueUnknown = extractTokens(value.raw).some((t) => !knows(t));
      const labelUnknown = extractTokens(label.raw).some((t) => !knows(t));
      if (valueUnknown || labelUnknown) {
        kept.push(field);
        if (valueUnknown) (uncertain[group] ??= []).push(field.key!);
        return;
      }

      if (rendererDropsRow(value.resolved, value.raw, label.resolved)) {
        dropped.push({ group, name: label.resolved.trim() || field.key });
        return;
      }
      kept.push(field);
    });
    out[group] = kept;
  }

  return { config: out, dropped, uncertain };
}

/**
 * The same drop rule, for the DESIGN lane — where there is no guest, and the
 * builder therefore knows about exactly one class of token.
 *
 * <p>`applyPassRenderRules` above needs a resolved config and a guest. The
 * builder's default view has neither, so it used to model nothing at all: it
 * drew every configured row, filled guest tokens from a table of invented
 * samples, and — for a token it had no sample for — drew the TOKEN'S OWN NAME
 * as if it were a value. That is the reported bug: an event with no venue
 * showed `ORT / eventLocation` in the builder and shipped no such row at all.
 *
 * <p>But the builder is opened FOR an event, so the event-level tokens are not
 * a guess. `buildEventVariables` skips empty sources, which makes an absent key
 * KNOWLEDGE — the send will find the same emptiness (`EVENT_VARIABLE_KEYS`
 * documents this, and `knowsToken` already acts on it in the guest lane). So:
 *
 * <ul>
 *   <li>a value whose tokens are ALL event-level can be decided here, and is
 *       dropped exactly as `PassJsonBuilder.putSection` would drop it;</li>
 *   <li>a value carrying any other token cannot be decided without a guest —
 *       it is kept and reported as UNCERTAIN, so the preview can dim it rather
 *       than promise it;</li>
 *   <li>with no real event loaded nothing is decidable and nothing is dropped,
 *       for the same reason the guest lane refuses to report an unfinished
 *       fetch as a short pass.</li>
 * </ul>
 */
export type EventTokenParity = {
  config: WalletConfig;
  /** Rows no guest will receive — the event itself has no such value. */
  droppedForEvent: DroppedField[];
  /** Rows that MAY drop per guest: `group` → the field keys to dim. */
  uncertain: Record<string, string[]>;
};

export function applyEventTokenRules(
  config: WalletConfig,
  opts: {
    lang: string;
    primaryLang: string;
    /**
     * Event-level token → the text it will ship as, as
     * `buildEventVariables` produced it. A key that is ABSENT means the event
     * carries no such value; that is the whole signal this function runs on.
     */
    eventValues: Record<string, string>;
    /** Whether a REAL event was loaded. Without one, nothing is decidable. */
    eventLoaded: boolean;
    /**
     * Whether the CONSUMING renderer looks token names up untrimmed, as
     * `TemplateVarSubstitutor` does for the `.pkpass`. False for the Google
     * card, whose `replaceVariables` trims and therefore resolves a padded
     * token. Defaults to true — Apple is the lane this rule was written for.
     */
    applePaddingRules?: boolean;
  },
): EventTokenParity {
  const { lang, primaryLang, eventValues, eventLoaded } = opts;
  const applePaddingRules = opts.applePaddingRules ?? true;
  const droppedForEvent: DroppedField[] = [];
  const uncertain: Record<string, string[]> = {};
  const out: WalletConfig = { ...config };

  for (const group of FIELD_GROUPS) {
    const fields = config[group];
    if (!fields || fields.length === 0) continue;
    const kept: WalletField[] = [];
    for (const field of fields) {
      // The renderer skips a keyless field outright, before any of this.
      if (!field || !notBlank(field.key)) continue;

      // `pickLangKey`, not `resolveLangValue` — same reason `pickPair` above
      // exists. The two disagree in their last-resort tier (insertion order vs
      // the backend's sorted order), and this rule DELETES rows, so reading the
      // wrong language's text here deletes the wrong row.
      const valueKey = pickLangKey(field.value, lang, primaryLang);
      const labelKey = pickLangKey(field.label, lang, primaryLang);
      const rawValue = valueKey ? (field.value?.[valueKey] ?? "") : "";
      const rawLabel = labelKey ? (field.label?.[labelKey] ?? "") : "";
      const tokens = [...extractTokens(rawValue), ...extractTokens(rawLabel)];
      // A PADDED token is decidable ON APPLE, whatever it names.
      //
      // The shared `extractTokens` trims, so `{{ firstName }}` arrives here as
      // "firstName" — not an event key, therefore "undecidable", therefore the
      // row is kept and captioned "may not reach this guest". But the renderer
      // looks the name up untrimmed and blanks a miss, so that row drops for
      // EVERY guest, always. The builder drew a label over an empty value and
      // called a certainty a risk.
      //
      // `substituteEventTokens` already blanks these; this just stops the
      // undecidable gate short-circuiting before it gets the chance. The
      // event-token half of this was fixed a round ago and the guest-token half
      // was not — the same one-of-two-call-sites shape, in the same function.
      //
      // ONLY ON APPLE, and that is not a detail. `main-consumer`'s
      // `replaceVariables` — the Google lane's substituter — does
      // `const key = raw.trim()`, so a padded token RESOLVES on the Google
      // card. This preview is shared: `WalletPreview` runs this rule once and
      // hands the result to both platforms. Applying the Apple truth to the
      // Google preview deletes a working row and lists it as one "no guest will
      // receive" — sending an operator to remove a field that works.
      //
      // Per OCCURRENCE, not per name. The first version tested
      // `rawValue.includes("{{" + name + " ")` against a TRIMMED name, which
      // caught a single leading or trailing space and missed two spaces, a tab,
      // a newline and a non-breaking space — all of which the renderer blanks
      // just the same — while a padded `{{ seatNumber }}` elsewhere in the row
      // also cleared an unpadded `{{seat}}`, silently dropping ITS at-risk
      // warning.
      const occurrences = [
        ...rawTokenOccurrences(rawValue),
        ...rawTokenOccurrences(rawLabel),
      ];
      const undecidable = occurrences.filter(
        (o) =>
          !EVENT_VARIABLE_KEYS.has(o.name) && !(applePaddingRules && o.padded),
      );

      if (!eventLoaded) {
        // No event at all — the preset gallery, the create wizard, the template
        // builder (`WalletTemplateBuilderPage` passes `eventId=""`). Keep the
        // row and say NOTHING about it.
        //
        // Marking these as at-risk is what the first version did, and it made
        // the template builder name every tokened row on every open. That is the
        // same always-on warning the dimming was removed for: a caption that
        // fires for everything is read as chrome, and the one time it names a
        // genuinely doomed row it is invisible. "We could not check" is not
        // "this may fail" — a row with a guest token in a template that has no
        // event yet is simply undecided, and undecided says nothing.
        kept.push(field);
        continue;
      }
      if (undecidable.length > 0) {
        // A REAL event is loaded and the row still carries a token this rule
        // cannot decide (a guest token). That is a genuine at-risk row, so it is
        // marked — but never deleted from under an operator who has not been
        // shown why. `undecidable` is non-empty only if `tokens` is.
        (uncertain[group] ??= []).push(field.key!);
        kept.push(field);
        continue;
      }

      const value = substituteEventTokens(rawValue, eventValues);
      const label = substituteEventTokens(rawLabel, eventValues);

      // The renderer's condition (`PassJsonBuilder.putSection`) with ONE
      // deliberate exemption, and this is the only place this module knowingly
      // disagrees with the delivered pass.
      //
      // A field with nothing in it at all is a row mid-authoring:
      // `WalletFieldEditor.addField` creates exactly `{key, label:{lang:""},
      // value:{lang:""}}`. The renderer does drop it — but deleting an
      // operator's own row at the instant they click "+", and naming it by the
      // internal key they have never seen, is worse than a blank row that heals
      // itself the moment they type. It survives here, unreported.
      //
      // The exemption also makes this function IDEMPOTENT, which is what the
      // second half of the bug needed. The guest lanes substitute before handing
      // the config over, so on their output `substituteEventTokens` is the
      // identity: `value === rawValue` and `label === rawLabel`. The condition
      // below then reduces to `rawValue === "" && rawLabel === ""` — precisely
      // `nothingConfigured`, precisely what is excluded. So a second pass cannot
      // delete anything, and the rows `applyPassRenderRules` deliberately
      // protects (an unresolvable guest token it refuses to decide about) stay.
      //
      // Scope: `applyPassRenderRules`, `putSection` and the shared parity
      // fixture keep the renderer's rule unchanged. Widening the exemption to
      // them would break the mirror the fixture exists to hold.
      const nothingConfigured = rawValue.length === 0 && rawLabel.length === 0;
      if (
        !nothingConfigured &&
        value.length === 0 &&
        (notBlank(rawValue) || label.length === 0)
      ) {
        droppedForEvent.push({ group, name: label.trim() || field.key! });
        continue;
      }
      kept.push(field);
    }
    out[group] = kept;
  }

  return { config: out, droppedForEvent, uncertain };
}

/**
 * The at-risk rows, named the way the operator can find them.
 *
 * Extracted from `WalletEditorBody` so it can be tested at all: it lived inline
 * in a component that no test mounts, and after the dimming was removed this
 * caption became the ONLY channel telling an operator that a row may not reach a
 * guest. Emptying it would have deleted the warning rather than relocated it,
 * with nothing firing.
 *
 * By LABEL, falling back to the key only when there is none — the same rule
 * `applyPassRenderRules` and `PassJsonBuilder.putSection` use for their own
 * reports, and for the same reason: an internal key like `field_1` names a row
 * the editor never shows.
 *
 * Selected per GROUP AND KEY and deduplicated per NAME — see the comments
 * inside; the two are separate jobs and doing only one of them costs either a
 * noisy sentence or, worse, a silenced warning.
 */
/**
 * What an UNDECIDABLE row will actually do on the delivered pass, where the
 * client can settle it.
 *
 * `applyPassRenderRules` reports a row as `uncertain` whenever a token in its
 * value is one this client cannot resolve. That is the honest answer to "what
 * does it say", and it is NOT the answer to "will the row be there" — and the
 * preview needs the second one, or it draws a promise over a row the guest
 * never receives.
 *
 * There is exactly ONE family where the second question is decidable here, and
 * it is the family the whole complaint was about. Seat tokens
 * ({@link SEAT_ENRICHED_KEYS}) are filled by the renderer from the seating plan
 * — but only when a seat exists. `guestHasSeat` settles that from `seatInfo` /
 * `seatCount`, both of which the projection carries:
 *
 *  - no seat  → `primarySeat(...)` is null, nothing is put, and
 *    `PassJsonBuilder.putSection` deletes the row. `"absent"`.
 *  - a seat   → the renderer fills it, on Apple AND on Google. `"awaited"`.
 *
 * EVERY token must be seat-enriched for `"absent"`. A mixed value like
 * `"Tisch {{tableNumber}} / {{foo}}"` is not settled by the seat fact alone —
 * `{{foo}}` could still produce text — so it stays `"awaited"`, which is the
 * conservative half: the row is drawn as awaiting a value rather than deleted.
 *
 * DELIBERATELY NOT SPLIT BY PLATFORM. An earlier version hid these rows on the
 * Google card on the argument that `main-consumer` only aliases a pushed
 * `seatIndex`. That was measured and is wrong in both directions:
 * `context-enricher.ts` resolves `{{seatList}}` by its own Mongo lookup on an
 * ordinary send, a seating trigger's context is spread AFTER `payloadFromGuest`
 * so `CANONICAL_KEYS` never filters `furnitureId`, and `ACTIVITY_NAME_KEYS`
 * rows — which are `uncertain` too — are filled by that very lane. Platform is
 * not the discriminator; knowledge is.
 *
 * KNOWN LIMIT, recorded rather than fixed: `uncertain` is keyed by field key
 * alone, while `applyPassRenderRules` disambiguates same-key repeats within a
 * group BY INDEX (see its comment above `matches`). Two rows sharing a key in
 * one group therefore both take the answer computed for one of them. The
 * editor's `nextFieldKey` numbers within a group, so reaching this needs an
 * import or a config patch; closing it means carrying indices in `uncertain`,
 * which is that function's contract and cross-repo pinned.
 */
export function classifyUncertainRows(
  config: WalletConfig,
  uncertain: Record<string, string[]>,
  opts: {
    /** `guestHasSeat`. The caller must have projected `seatInfo` — see there. */
    hasSeat: boolean;
    /** `activityRollupEmpty` for one token, tri-state for the same reason. */
    rollupEmpty: (token: string) => boolean | undefined;
    lang: string;
    primaryLang: string;
  },
): Partial<Record<FieldGroup, Record<string, RowCertainty>>> {
  const out: Partial<Record<FieldGroup, Record<string, RowCertainty>>> = {};
  for (const group of FIELD_GROUPS) {
    const keys = uncertain[group];
    if (!keys || keys.length === 0) continue;
    const wanted = new Set(keys);
    const seen = new Set<string>();
    for (const f of config[group] ?? []) {
      if (!f?.key || !wanted.has(f.key)) continue;
      seen.add(f.key);
      // `pickLangKey`, for the same reason `namedUncertainRows` uses it: the
      // verdict must be read off the text the RULE read, not off the editor's
      // open tab. The two disagree whenever the guest's language is not the
      // one being edited.
      const valueKey = pickLangKey(f.value, opts.lang, opts.primaryLang);
      const raw = valueKey ? (f.value?.[valueKey] ?? "") : "";
      (out[group] ??= {})[f.key] = verdictFor(raw, opts);
    }
    // A key `applyPassRenderRules` reported but this config does not hold under
    // that name. It tolerates the same drift by falling back to the INDEX when
    // a group's keys were rewritten between the raw and the resolved config, so
    // the pairing can legitimately fail here. Without this the row fell out of
    // both sentences at once and went entirely unexplained — worse than the
    // conservative answer, which is that a value may still be coming.
    for (const k of keys) {
      if (!seen.has(k)) (out[group] ??= {})[k] = "awaited";
    }
  }
  return out;
}

/**
 * One row's fate, from its raw value.
 *
 * `"absent"` needs POSITIVE evidence of no value, and there are exactly two
 * families that supply it. Everything else is `"awaited"` — the conservative
 * half, because a bar over a row that does arrive is a smaller error than
 * deleting a row the guest receives.
 *
 * THE LITERAL TEXT COUNTS. An earlier version looked only at the tokens, so
 * `"Tisch {{tableNumber}}"` was judged as if it were the bare token — but the
 * substitutor blanks the token and leaves `"Tisch "`, which is not empty, so
 * `PassJsonBuilder.putSection` KEEPS the row and the card draws it. The warning
 * sentence then named a row the operator could see in front of them. No
 * trimming here, deliberately: the renderer's test is `value.isEmpty()`, so a
 * value of `"{{tableNumber}} "` ships a single space and survives too.
 */
function verdictFor(
  raw: string,
  opts: {
    hasSeat: boolean;
    rollupEmpty: (token: string) => boolean | undefined;
  },
): RowCertainty {
  const tokens = extractTokens(raw);
  if (tokens.length === 0) return "awaited";
  // Whatever is NOT a token survives substitution, and any survivor keeps the
  // row. `rawTokenOccurrences`' pattern, so padding is stripped the same way.
  if (raw.replace(/\{\{[^{}]+\}\}/g, "").length > 0) return "awaited";

  if (tokens.every((t) => SEAT_ENRICHED_KEYS.has(t))) {
    return opts.hasSeat ? "awaited" : "absent";
  }
  // The activity family: every token must be one whose rollup is KNOWN empty.
  // A mixed row, or one whose rollup the projection did not carry, stays
  // `"awaited"` — `undefined` is not `true`.
  if (tokens.every((t) => opts.rollupEmpty(t) === true)) return "absent";
  return "awaited";
}

/**
 * The rows with one verdict, in the shape `namedUncertainRows` reads.
 *
 * Two sentences under the card describe two opposite fates — a row the renderer
 * fills, and a row it deletes — and both are built by the same namer. This is
 * the adapter between the verdict map and that namer, so neither of them has to
 * know about the other's shape.
 */
export function pickByCertainty(
  rows: Partial<Record<FieldGroup, Record<string, RowCertainty>>>,
  want: RowCertainty,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const group of FIELD_GROUPS) {
    const byKey = rows[group];
    if (!byKey) continue;
    const keys = Object.entries(byKey)
      .filter(([, c]) => c === want)
      .map(([k]) => k);
    if (keys.length > 0) out[group] = keys;
  }
  return out;
}

export function namedUncertainRows(
  config: WalletConfig,
  uncertain: Record<string, string[]>,
  lang: string,
  primaryLang: string,
): string[] {
  // Keyed by GROUP AND KEY, not by key alone. Apple requires globally unique
  // field keys and `PassJsonBuilder` appends `_2`, but the CONFIG an operator
  // edits routinely carries the same key in two groups. A flat set let the
  // first group in `FIELD_GROUPS` order answer for a key marked in a later one:
  // a `seat` row in `headerFields` that is perfectly safe would be named, and
  // the `seat` row in `backFields` that is actually at risk would be suppressed
  // as a duplicate. The version before the dedup named both — noisy, but never
  // wrong. Narrowing it must not cost the right answer.
  const wanted = new Set<string>();
  for (const [group, keys] of Object.entries(uncertain)) {
    for (const k of keys) wanted.add(`${group}\u0000${k}`);
  }
  if (wanted.size === 0) return [];
  const names: string[] = [];
  // Deduplicated by the NAME the operator reads, not by the key.
  //
  // These are two different jobs and the first version did only one of them.
  // Selecting by group+key is the CORRECTNESS half: a safe `seat` row in
  // `headerFields` must never answer for the at-risk `seat` row in `backFields`
  // — with a flat key set the safe one won on `FIELD_GROUPS` order and
  // suppressed the row that was actually in danger. Deduplicating by name is
  // the COSMETIC half: two at-risk rows that both read "Ort" are one thing to
  // go and look at, so the sentence says it once. Rows that are not at risk are
  // never candidates here at all, so nothing can be silenced by a namesake.
  const seen = new Set<string>();
  for (const group of FIELD_GROUPS) {
    for (const f of config[group] ?? []) {
      if (!f?.key) continue;
      const id = `${group}\u0000${f.key}`;
      if (!wanted.has(id)) continue;
      // `pickLangKey`, for the same reason `applyEventTokenRules` uses it: the
      // caption must name the row in the language whose text the RULE read. The
      // two resolvers disagree in their last-resort tier, so `resolveLangValue`
      // here could label a row from one language while the decision to flag it
      // was made on another.
      const labelKey = pickLangKey(f.label, lang, primaryLang);
      const label = (labelKey ? (f.label?.[labelKey] ?? "") : "").trim();
      const name = label || f.key;
      if (seen.has(name)) continue;
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

/**
 * Every `{{token}}` in a string, with the name the RENDERER would look up and
 * whether it carries padding the renderer will not tolerate.
 *
 * `extractTokens` (shared, and feeding the email and PDF previews too) trims,
 * which is right for those lanes and wrong here: `TemplateVarSubstitutor` looks
 * the captured group up untrimmed. Occurrence-level, because one row can hold a
 * padded and an unpadded token at once and they have different fates.
 */
function rawTokenOccurrences(text: string): { name: string; padded: boolean }[] {
  return [...text.matchAll(/\{\{([^{}]+)\}\}/g)].map((m) => {
    const raw = m[1] ?? "";
    return { name: raw.trim(), padded: raw !== raw.trim() };
  });
}

/** Replace the tokens the builder can decide; leave every other one standing. */
function substituteEventTokens(
  text: string,
  eventValues: Record<string, string>,
): string {
  return text.replace(/\{\{([^{}]+)\}\}/g, (token, rawName: string) => {
    // `TemplateVarSubstitutor.substitute` looks the name up EXACTLY as the
    // regex captured it (`String key = m.group(1)` — no trim) and blanks a
    // miss. No payload key carries surrounding spaces, so `{{ eventLocation }}`
    // misses on the renderer whatever it names, and the pass ships nothing.
    //
    // The preview used to trim first and draw the venue: a row visible in the
    // builder, absent from the pass, and not even reported as at-risk, because
    // the substitution here had "succeeded". Blanking it instead puts the row
    // through the ordinary drop rule below, so the operator is told.
    //
    // NOTE, and deliberately not fixed here: the shared `extractTokens`
    // (`lib/utils/variable-replacer.ts:280`) also trims, and it feeds the email
    // and PDF preview lanes too. Its regex was aligned with the backend's; its
    // trim was not. Widening that is a change to three renderers' previews and
    // does not belong in a wallet fix.
    if (rawName !== rawName.trim()) return "";
    if (!EVENT_VARIABLE_KEYS.has(rawName)) return token;
    return eventValues[rawName] ?? "";
  });
}

/**
 * The colour this value will actually put on the pass, or null for one the
 * renderer throws away.
 *
 * Character for character `PassJsonBuilder.hexToRgb`: six hex digits, with or
 * without a leading `#`, and nothing else. A value it rejects becomes `null`,
 * and `putIfPresent` then leaves the key out of `pass.json` altogether — so the
 * DEVICE falls back to its own default. The preview used to hand the raw string
 * to a CSS `style`, which accepts `rgba(...)`, `transparent`, `#abc`, `red` and
 * more; every one of those drew a colour the guest would never see. Note the
 * inversion in the last case: `aabbcc` without a `#` is valid to the renderer
 * and invalid to CSS, so passing it through unchanged was wrong in the other
 * direction too.
 */
export function passColorOrNull(raw: string | undefined | null): string | null {
  if (typeof raw !== "string") return null;
  const h = raw.startsWith("#") ? raw.slice(1) : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return `#${h}`;
}

/**
 * Practical width of the pass-face brand line before it reaches the header
 * fields. Apple gives no character limit — the constraint is the shared header
 * row, and this is the length that still leaves room for a date. Mirrors
 * `PassJsonBuilder.LOGO_TEXT_MAX_CHARS`.
 */
export const LOGO_TEXT_MAX_CHARS = 24;

/**
 * Which text ends up on the pass face, and whether it got there by fallback.
 *
 * <p>Apple draws `logoText`; `organizationName` is metadata. The renderer falls
 * back to the organisation name so the brand still appears — and caps ONLY that
 * fallback, because an operator who typed the line themselves chose its length,
 * while nobody chose to have their legal company name pushed onto the face.
 */
export function brandLineTemplate(
  config: WalletConfig,
  lang: string,
  primaryLang: string,
): { text: string; isFallback: boolean } {
  const logoText = resolveLangValue(config.logoText, lang, primaryLang, "");
  if (notBlank(logoText)) return { text: logoText, isFallback: false };
  return {
    text: resolveLangValue(config.organizationName, lang, primaryLang, ""),
    isFallback: true,
  };
}

/** Apply the fallback's cap to an already-resolved brand line. */
export function capBrandLine(shipped: string, isFallback: boolean): string {
  return isFallback ? ellipsize(shipped, LOGO_TEXT_MAX_CHARS) : shipped;
}

/**
 * Trim to `max` characters on a word boundary where one is close by. Character
 * for character the renderer's `PassJsonBuilder.ellipsize` — including that the
 * ellipsis is one character and the budget already accounts for it.
 */
export function ellipsize(s: string, max: number): string {
  if (s.length <= max) return s;
  let cut = s.substring(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  // Only break on a word if that doesn't throw away most of the line.
  if (lastSpace > max / 2) cut = cut.substring(0, lastSpace);
  return cut.trim() + "…";
}
